/**
 * Book of Lessons — exhaustive condition evaluation.
 *
 * When a tap or crystallization advances state, we walk the catalog of
 * lessons the player has yet to unlock and evaluate each condition against
 * the new state. Newly satisfied lessons are reported back; the caller
 * persists them and shows a UI moment.
 */
import { computeAtlasProgress } from './atlas.js';
import type {
  GameState,
  LessonCondition,
  LessonDefinition,
  PhraseEntry,
  PhraseLayer,
} from './types.js';

export interface EvaluateLessonsInput {
  readonly state: GameState;
  readonly catalog: readonly LessonDefinition[];
  readonly allPhrases: readonly PhraseEntry[];
  /** Now, in epoch ms. Used by time-of-day and return-after conditions. */
  readonly now: number;
}

/**
 * Returns the subset of catalog lessons that:
 *   1. Are not yet in `state.lessonsUnlocked`, AND
 *   2. Have their condition satisfied by the current state.
 */
export function evaluateLessons(input: EvaluateLessonsInput): readonly LessonDefinition[] {
  const newlyUnlocked: LessonDefinition[] = [];
  for (const lesson of input.catalog) {
    if (input.state.lessonsUnlocked.has(lesson.id)) {
      continue;
    }
    if (isConditionMet(lesson.condition, input)) {
      newlyUnlocked.push(lesson);
    }
  }
  return newlyUnlocked;
}

/**
 * Sum all lesson bonuses into the additive `lessonsBonus` term used by the
 * global multiplier composition.
 */
export function computeLessonsBonus(
  unlockedIds: ReadonlySet<string>,
  catalog: readonly LessonDefinition[],
): number {
  let bonus = 0;
  for (const lesson of catalog) {
    if (unlockedIds.has(lesson.id)) {
      bonus += lesson.bonus;
    }
  }
  return bonus;
}

// ---------------------------------------------------------------------------
// Condition evaluator — exhaustive switch enforces new condition kinds
// ---------------------------------------------------------------------------

function isConditionMet(condition: LessonCondition, input: EvaluateLessonsInput): boolean {
  const { state, now, allPhrases } = input;

  switch (condition.kind) {
    case 'firstTap':
      return state.stats.totalTaps >= 1;

    case 'awarenessesAtLeast':
      return state.resources.awarenesses >= condition.value;

    case 'spiritBallsAtLeast':
      return state.resources.spirit_balls >= condition.value;

    case 'tapsInRhythm':
      return state.stats.tapsInRhythmBest >= condition.value;

    case 'crystallizationsAtLeast':
      return state.stats.totalCrystallizations >= condition.value;

    case 'returnAfterHours': {
      if (state.stats.lastActiveAt === 0) {
        return false;
      }
      const hoursSince = (now - state.stats.lastActiveAt) / (1000 * 60 * 60);
      return hoursSince >= condition.value;
    }

    case 'daysStreak':
      return state.stats.daysStreak >= condition.value;

    case 'tapAtHour': {
      if (state.stats.lastTapAt === null) {
        return false;
      }
      const hour = new Date(state.stats.lastTapAt).getHours();
      return hour >= condition.fromHour && hour < condition.toHour;
    }

    case 'collectPhrasesInEpoch': {
      const progress = computeAtlasProgress(state.atlasCollected, allPhrases).find(
        (p) => p.epoch === condition.epoch,
      );
      return progress ? progress.collected >= condition.count : false;
    }

    case 'collectAllPhrasesInEpoch': {
      const progress = computeAtlasProgress(state.atlasCollected, allPhrases).find(
        (p) => p.epoch === condition.epoch,
      );
      return progress ? progress.complete : false;
    }

    case 'collectAllPhrasesInLayer':
      return allPhrasesInLayerCollected(allPhrases, state.atlasCollected, condition.layer);

    case 'reachEpoch':
      return state.epoch >= condition.epoch;

    case 'silenceSecondsAtLeast': {
      if (state.stats.lastTapAt === null) {
        return false;
      }
      const secondsSince = (now - state.stats.lastTapAt) / 1000;
      return secondsSince >= condition.value;
    }

    case 'profileNameSet':
      return state.profile.displayName !== null && state.profile.displayName.length > 0;

    case 'goldenSparkCaught':
      return state.stats.totalGoldenSparksCaught >= condition.count;

    default:
      return assertNever(condition);
  }
}

function allPhrasesInLayerCollected(
  allPhrases: readonly PhraseEntry[],
  atlas: ReadonlyMap<string, number>,
  layer: PhraseLayer,
): boolean {
  let found = 0;
  for (const phrase of allPhrases) {
    if (phrase.layer === layer) {
      found += 1;
      if (!atlas.has(phrase.id)) {
        return false;
      }
    }
  }
  return found > 0;
}

function assertNever(value: never): never {
  throw new Error(`Unhandled lesson condition: ${JSON.stringify(value)}`);
}
